import { StarPicker } from 'components/StarPicker';
import { StarDisplay } from 'components/StarDisplay';
import { formatDateShort } from 'utils/formatters';

import * as styled from './styles';

type Props = {
  step: EditReview.Step;
  contact: string;
  code: string;
  reviews: Kudable.EditableReview[];
  editing: Kudable.EditableReview | null;
  overallRating: number;
  comment: string;
  improvementSuggestion: string;
  improvementPublic: boolean;
  busy: boolean;
  error: string | null;
  onContactChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onRequestOtp: () => void;
  onVerifyOtp: () => void;
  onStartEdit: (review: Kudable.EditableReview) => void;
  onOverallRatingChange: (value: number) => void;
  onCommentChange: (value: string) => void;
  onImprovementSuggestionChange: (value: string) => void;
  onImprovementPublicChange: (value: boolean) => void;
  onSaveEdit: () => void;
  onStartOver: () => void;
  onGoToProfile?: () => void;
};

export const EditReviewScene = ({
  step,
  contact,
  code,
  reviews,
  editing,
  overallRating,
  comment,
  improvementSuggestion,
  improvementPublic,
  busy,
  error,
  onContactChange,
  onCodeChange,
  onRequestOtp,
  onVerifyOtp,
  onStartEdit,
  onOverallRatingChange,
  onCommentChange,
  onImprovementSuggestionChange,
  onImprovementPublicChange,
  onSaveEdit,
  onStartOver,
  onGoToProfile,
}: Props) => (
  <div>
    <styled.TopBar>
      <styled.Logo>Kudable</styled.Logo>
    </styled.TopBar>

    <styled.Container>
      <styled.Card>
        <styled.Title>Edit a review</styled.Title>

        {step === 'contact' && (
          <>
            <styled.Subtitle>
              Enter the phone number or email you used when leaving the review.
              We'll send you a code.
            </styled.Subtitle>
            <styled.Input
              placeholder="Phone or email"
              value={contact}
              onChange={(event) => onContactChange(event.target.value)}
            />
            {error && <styled.ErrorText>{error}</styled.ErrorText>}
            <styled.PrimaryBtn
              onClick={onRequestOtp}
              disabled={busy || !contact.trim()}
            >
              {busy ? 'Sending…' : 'Send code'}
            </styled.PrimaryBtn>
          </>
        )}

        {step === 'otp' && (
          <>
            <styled.Subtitle>
              Enter the 6-digit code we sent to <strong>{contact}</strong>.
            </styled.Subtitle>
            <styled.CodeInput
              placeholder="000000"
              value={code}
              onChange={(event) => onCodeChange(event.target.value)}
              maxLength={6}
              autoFocus
            />
            {error && <styled.ErrorText>{error}</styled.ErrorText>}
            <styled.PrimaryBtn
              onClick={onVerifyOtp}
              disabled={busy || code.length < 6}
            >
              {busy ? 'Verifying…' : 'Verify'}
            </styled.PrimaryBtn>
            <styled.LinkBtn onClick={onStartOver}>
              Use a different contact
            </styled.LinkBtn>
          </>
        )}

        {step === 'list' && (
          <>
            <styled.Subtitle>
              Pick the review you'd like to edit. You can update the rating and
              comment — your edit will be visible.
            </styled.Subtitle>
            <styled.ReviewList>
              {reviews.map((review) => (
                <styled.ReviewRow key={review.id} onClick={() => onStartEdit(review)}>
                  {review.service_name && (
                    <styled.ReviewService>
                      {review.service_name}
                    </styled.ReviewService>
                  )}
                  <StarDisplay value={review.overall_rating} size={14} />
                  <styled.ReviewDate>
                    {formatDateShort(review.inserted_at)}
                  </styled.ReviewDate>
                  {review.comment && (
                    <styled.ReviewComment>"{review.comment}"</styled.ReviewComment>
                  )}
                </styled.ReviewRow>
              ))}
            </styled.ReviewList>
            <styled.LinkBtn onClick={onStartOver}>Done</styled.LinkBtn>
          </>
        )}

        {step === 'edit' && editing && (
          <>
            <styled.EditHeader>
              {editing.service_name && (
                <styled.EditServiceName>
                  {editing.service_name}
                </styled.EditServiceName>
              )}
              <styled.EditingHint>
                Editing your review from {formatDateShort(editing.inserted_at)}
              </styled.EditingHint>
            </styled.EditHeader>

            <styled.OriginalBox>
              <styled.OriginalHeading>
                Your original review
              </styled.OriginalHeading>
              <styled.OriginalStarsRow>
                <StarDisplay value={editing.overall_rating} size={14} />
              </styled.OriginalStarsRow>
              {editing.comment && (
                <styled.OriginalText>"{editing.comment}"</styled.OriginalText>
              )}
              {editing.improvement_suggestion && (
                <styled.OriginalText>
                  💡 "{editing.improvement_suggestion}"
                </styled.OriginalText>
              )}
            </styled.OriginalBox>

            <styled.FieldLabel>Updated rating</styled.FieldLabel>
            <StarPicker
              value={overallRating}
              onChange={onOverallRatingChange}
            />

            <styled.FieldLabel>What went well? (optional)</styled.FieldLabel>
            <styled.Textarea
              value={comment}
              onChange={(event) => onCommentChange(event.target.value)}
              placeholder="Leave empty to keep your original comment"
            />

            <styled.FieldLabel>
              What could they improve? (optional)
            </styled.FieldLabel>
            <styled.Textarea
              value={improvementSuggestion}
              onChange={(event) => onImprovementSuggestionChange(event.target.value)}
              placeholder="Leave empty to keep your original feedback"
            />

            {improvementSuggestion.trim().length > 0 && (
              <styled.ToggleBox>
                <styled.ToggleTitle>
                  Who can see this feedback?
                </styled.ToggleTitle>
                <styled.RadioRow>
                  <input
                    type="radio"
                    checked={!improvementPublic}
                    onChange={() => onImprovementPublicChange(false)}
                  />
                  <span>Only the provider (private)</span>
                </styled.RadioRow>
                <styled.RadioRow>
                  <input
                    type="radio"
                    checked={improvementPublic}
                    onChange={() => onImprovementPublicChange(true)}
                  />
                  <span>Show on the public profile</span>
                </styled.RadioRow>
              </styled.ToggleBox>
            )}

            {error && <styled.ErrorText>{error}</styled.ErrorText>}

            <styled.PrimaryBtn
              onClick={onSaveEdit}
              disabled={busy || overallRating === 0}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </styled.PrimaryBtn>
            <styled.LinkBtn onClick={onStartOver}>Cancel</styled.LinkBtn>
          </>
        )}

        {step === 'done' && (
          <styled.SuccessBox>
            <styled.SuccessIcon>✓</styled.SuccessIcon>
            <styled.SuccessTitle>Review updated</styled.SuccessTitle>
            <styled.Subtitle>
              Your changes are live. The review is now marked as edited so the
              provider knows it's been updated.
            </styled.Subtitle>
            {onGoToProfile && (
              <styled.PrimaryBtn onClick={onGoToProfile}>
                View their profile
              </styled.PrimaryBtn>
            )}
            <styled.LinkBtn onClick={onStartOver}>
              Edit another review
            </styled.LinkBtn>
          </styled.SuccessBox>
        )}
      </styled.Card>
    </styled.Container>
  </div>
);
